import { useLayoutEffect, useMemo, useRef, useState } from "react";

type ActivityDay = { date: string; count: number };
type CalendarDay = { date: string; count: number; level: 0 | 1 | 2 | 3 | 4; placeholder?: boolean };
type CalendarWeek = { label: string; days: CalendarDay[] };

// 开发期沿用繁星宇宙个人主页的创作日历预览分布；不写入学员的真实创作记录。
function seededNoise(index: number) {
  const value = Math.sin(index * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function buildCalendarWeeks(selectedYear: number): CalendarWeek[] {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const currentYear = today.getFullYear();
  const rollingYear = selectedYear === currentYear;
  const startDate = rollingYear ? new Date(today.getTime() - 370 * 86400000) : new Date(selectedYear, 0, 1, 12);
  const endDate = rollingYear ? today : new Date(selectedYear, 11, 31, 12);
  startDate.setHours(12, 0, 0, 0);
  const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
  const yearOffset = (selectedYear - 2024) * 97 + (rollingYear ? 41 : 0);
  const spotlightStart = rollingYear || selectedYear === 2026 ? 0.72 : 0.22;
  const yearBias = rollingYear ? 0.18 : selectedYear === 2026 ? 0.2 : 0.06;
  const days: CalendarDay[] = [];

  for (let index = 0; index < totalDays; index += 1) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + index);
    const weekend = date.getDay() === 0 || date.getDay() === 6;
    const progress = index / Math.max(1, totalDays - 1);
    const wave = (Math.sin(index / 13) + 1) / 2;
    const seasonal = (Math.cos((date.getMonth() / 11) * Math.PI * 2 - Math.PI / 4) + 1) / 2;
    const spotlight = progress > spotlightStart ? 0.34 : progress > spotlightStart - 0.1 ? 0.18 : 0;
    const score = seededNoise(yearOffset + index) * 0.8 + wave * 0.52 + seasonal * 0.34 + yearBias + spotlight + (weekend ? -0.34 : 0.06);
    const level = score < 0.95 ? 0 : score < 1.32 ? 1 : score < 1.68 ? 2 : score < 2.02 ? 3 : 4;
    const count = level === 4 ? 5 : level;
    days.push({ date: dateKey(date), count, level });
  }

  const padded: CalendarDay[] = Array.from({ length: startDate.getDay() }, (_, index) => ({ date: `placeholder-${index}`, count: 0, level: 0, placeholder: true }));
  padded.push(...days);
  while (padded.length % 7) padded.push({ date: `placeholder-${padded.length}`, count: 0, level: 0, placeholder: true });

  return Array.from({ length: padded.length / 7 }, (_, index) => {
    const week = padded.slice(index * 7, index * 7 + 7);
    const firstDay = week.find((day) => !day.placeholder);
    const monthStart = week.find((day) => !day.placeholder && Number(day.date.slice(-2)) === 1);
    const labelDay = monthStart || (index === 0 ? firstDay : undefined);
    return { label: labelDay ? `${Number(labelDay.date.slice(5, 7))}月` : "", days: week };
  });
}

const previewMetrics = [
  ["连续创作", "18", "天"],
  ["累计创作", "46", "天"],
  ["完成任务", "27", "个"],
  ["提交版本", "63", "次"],
  ["通过里程碑", "12", "个"],
  ["完成成片", "18.6", "分钟"],
] as const;

export default function PublicActivityCalendar({ visible }: { activity: ActivityDay[]; visible: boolean }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const weeks = useMemo(() => buildCalendarWeeks(year), [year]);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // 与宇宙个人主页相同：窄屏展示日历靠近当前日期的一端。
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const align = () => { scroller.scrollLeft = window.innerWidth > 980 ? 0 : scroller.scrollWidth - scroller.clientWidth; };
    align();
    window.addEventListener("resize", align);
    return () => window.removeEventListener("resize", align);
  }, [year]);

  return <section className="yycp-activityCard yycp-activityCardEmbedded" aria-label="创作日历">
    <div className="yycp-activityShellEmbedded">
      <div className="yycp-activityInner yycp-activityInnerEmbedded">
        <div className="yycp-activityContent yycp-activityContentEmbedded">
          <div ref={scrollerRef} className="yycp-activityScroller yycp-activityScrollerEmbedded">
            <div className="yycp-activityCalendar">
              <div className="yycp-activityMonthsRow" aria-hidden="true">
                <div className="yycp-activityWeekdaySpacer" />
                <div className="yycp-activityMonthsTrack">{weeks.map((week, index) => <span key={index} className="yycp-activityMonthCell">{week.label}</span>)}</div>
              </div>
              <div className="yycp-activityMatrix">
                <div className="yycp-activityWeekdays" aria-hidden="true">{["", "一", "", "三", "", "五", ""].map((label, index) => <span key={index} className="yycp-activityWeekday">{label}</span>)}</div>
                <div className="yycp-activityWeeks">{weeks.map((week, weekIndex) => <div key={weekIndex} className="yycp-activityWeek">{week.days.map((day) => <span
                  key={day.date}
                  className={`yycp-activityCell yycp-activityLevel${visible ? day.level : 0}${day.placeholder ? " yycp-activityCellPlaceholder" : ""}`}
                  title={!visible || day.placeholder ? undefined : `${day.date} · ${day.count} 次创作活跃（开发预览）`}
                  aria-label={!visible || day.placeholder ? undefined : `${day.date} · ${day.count} 次创作活跃（开发预览）`}
                />)}</div>)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="yycp-activityYearRail"><div className="yycp-activityYearButtons" aria-label="切换年份">{[currentYear, currentYear - 1].map((option) => <button key={option} type="button" className={`yycp-activityYearBadge${year === option ? " yycp-activityYearBadgeActive" : ""}`} aria-pressed={year === option} onClick={() => setYear(option)}>{option}</button>)}</div></div>
      <div className="yycp-activityEmbeddedInfoPanel creator-activity-metrics">{visible ? <>
        {previewMetrics.map(([label, value, unit]) => <span key={label} className="yycp-activityEmbeddedMeta" title="开发期演示数据">{label} <strong className="yycp-activityFooterStrong">{value}</strong> {unit}</span>)}
        <div className="yycp-activityLegend yycp-activityEmbeddedLegend" aria-hidden="true"><span>低</span><div className="yycp-activityLegendScale">{[0, 1, 2, 3, 4].map((level) => <span key={level} className={`yycp-activityLegendDot yycp-activityLevel${level}`} />)}</div><span>高</span></div>
      </> : <span className="yycp-activityEmbeddedMeta">作者尚未公开创作活跃记录</span>}</div>
    </div>
  </section>;
}
